import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Shield, Award, Heart, MessageCircle } from 'lucide-react';

const GUIDE_IMAGE = "https://customer-assets.emergentagent.com/job_703a842f-a54d-49a9-b401-d9b355165d79/artifacts/jybapz4s_Jonatas%20Scharf.jpg";

const credentials = [
  { icon: Shield, text: "Certified Facilitator" },
  { icon: Award, text: "15+ Years Experience" },
  { icon: Heart, text: "Traditional Training" },
];

export const GuideSection = ({ whatsappLink }) => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  return (
    <section id="guide" className="py-24 relative overflow-hidden" data-testid="guide-section">
      {/* Background Glow Effect */}
      <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-neon-magenta/10 rounded-full blur-[150px]" />
      <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-egyptian-gold/10 rounded-full blur-[150px]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Image Column */}
          <motion.div
            ref={sectionRef}
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            {/* Holographic Frame */}
            <div className="relative">
              {/* Outer glow */}
              <div className="absolute -inset-4 bg-gradient-to-r from-neon-cyan via-neon-magenta to-egyptian-gold rounded-3xl opacity-30 blur-xl" />
              
              {/* Main image container */}
              <div className="relative glass-card rounded-3xl p-4 neon-border">
                <img
                  src={GUIDE_IMAGE}
                  alt="Jonatas Scharf - Your Guide"
                  className="w-full h-[500px] object-cover rounded-2xl"
                  data-testid="guide-image"
                />
                
                {/* Holographic overlay */}
                <div className="absolute inset-4 rounded-2xl bg-gradient-to-t from-jungle-dark/80 via-transparent to-transparent" />
                
                {/* Name badge */}
                <div className="absolute bottom-8 left-8 right-8">
                  <div className="glass-card rounded-xl p-4">
                    <p className="font-accent text-egyptian-gold text-sm tracking-widest mb-1">
                      YOUR GUIDE
                    </p>
                    <h3 className="font-heading font-bold text-2xl text-white uppercase">
                      Jonatas Scharf
                    </h3>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Content Column */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="font-accent text-neon-cyan text-sm tracking-[0.3em] mb-4">
              MEET YOUR SHAMAN-PILOT
            </p>
            
            <h2 className="font-heading font-bold text-4xl sm:text-5xl uppercase tracking-tight text-white mb-6">
              Guided by <span className="neon-gold">Wisdom</span>
            </h2>
            
            <p className="font-body text-gray-400 text-lg leading-relaxed mb-8">
              With over 15 years of dedicated practice and traditional training from indigenous masters 
              of the Amazon, Jonatas brings a unique blend of ancestral knowledge and modern understanding 
              to each ceremony. His approach honors the sacred traditions while ensuring the highest 
              standards of safety and care.
            </p>

            {/* Credentials */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              {credentials.map((cred, index) => {
                const Icon = cred.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                    className="flex items-center gap-3 glass-card rounded-xl p-4"
                    data-testid={`guide-credential-${index}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-egyptian-gold/20 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-egyptian-gold" />
                    </div>
                    <span className="font-heading text-sm text-white uppercase tracking-wide">
                      {cred.text}
                    </span>
                  </motion.div>
                );
              })}
            </div>

            <p className="font-body text-gray-400 leading-relaxed mb-8">
              "Every journey is sacred. I am here to hold space for your transformation, 
              to guide you through the depths and back, ensuring you feel supported at every step. 
              The medicine shows you the way—I am simply the keeper of the vessel."
            </p>

            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-btn inline-flex"
              data-testid="guide-whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" />
              Connect with Jonatas
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
